import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-white/10 bg-[#0D0D0D] px-4 py-3.5 text-sm text-white placeholder:text-[#4d4d4d] outline-none transition-colors duration-300 focus:border-[#D4AF37]";

function formatDetail(detail) {
  if (detail == null) return "Errore imprevisto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(
    () => localStorage.getItem("cz_admin_token") || ""
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [spotsInfo, setSpotsInfo] = useState(null);
  const [spotsTotal, setSpotsTotal] = useState("");
  const [savingSpots, setSavingSpots] = useState(false);

  const fetchCandidature = async (tk) => {
    try {
      const res = await axios.get(`${API}/admin/candidature`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setData(res.data);
      const spotsRes = await axios.get(`${API}/spots`);
      setSpotsInfo(spotsRes.data);
      setSpotsTotal(String(spotsRes.data.totale));
    } catch (e) {
      localStorage.removeItem("cz_admin_token");
      setToken("");
      setData(null);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/candidature/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "candidature-cortisolo-zero.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV scaricato.");
    } catch (e) {
      toast.error("Export non riuscito. Riprova.");
    }
  };

  const saveSpots = async (e) => {
    e.preventDefault();
    const totale = Number(spotsTotal);
    if (!Number.isInteger(totale) || totale < 0) {
      toast.error("Inserisci un numero intero valido.");
      return;
    }
    setSavingSpots(true);
    try {
      const res = await axios.put(
        `${API}/admin/spots`,
        { totale },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSpotsInfo(res.data);
      toast.success(`Posti totali aggiornati a ${totale}.`);
    } catch (e2) {
      toast.error("Aggiornamento non riuscito.");
    } finally {
      setSavingSpots(false);
    }
  };

  useEffect(() => {
    if (token) fetchCandidature(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("cz_admin_token", res.data.access_token);
      setToken(res.data.access_token);
      toast.success("Accesso effettuato.");
    } catch (e2) {
      setError(formatDetail(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("cz_admin_token");
    setToken("");
    setData(null);
    toast.success("Sessione terminata.");
  };

  return (
    <div
      data-testid="admin-page"
      className="min-h-screen bg-[#0D0D0D] px-6 py-14 text-white"
    >
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between border-b border-[#D4AF37]/20 pb-6">
          <span className="font-serif-lux text-lg tracking-[0.25em]">
            CORTISOLO <span className="text-[#D4AF37]">ZERO</span>
            <span className="ml-4 text-xs uppercase tracking-[0.3em] text-[#737373]">
              Area Riservata
            </span>
          </span>
          {token ? (
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="border border-[#B87333] px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[#D2853D] transition-colors duration-300 hover:bg-[#B87333] hover:text-white"
            >
              Esci
            </button>
          ) : (
            <a
              href="/"
              data-testid="admin-back-home"
              className="text-xs font-medium uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:text-[#D4AF37]"
            >
              Torna al sito
            </a>
          )}
        </div>

        {!token ? (
          <motion.form
            onSubmit={handleLogin}
            data-testid="admin-login-form"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mt-20 max-w-md space-y-6 border border-[#D4AF37]/25 bg-[#141414] p-10"
          >
            <h1 className="font-serif-lux text-2xl text-[#D4AF37]">
              Accesso Riservato
            </h1>
            {error && (
              <p
                data-testid="admin-login-error"
                className="border border-red-900/50 bg-red-950/30 px-4 py-3 text-xs text-red-300"
              >
                {error}
              </p>
            )}
            <div>
              <label
                htmlFor="admin-email"
                className="mb-2.5 block text-[11px] font-semibold uppercase tracking-[0.25em] text-[#737373]"
              >
                Email
              </label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@joe-fenix-method.com"
                className={inputCls}
              />
            </div>
            <div>
              <label
                htmlFor="admin-password"
                className="mb-2.5 block text-[11px] font-semibold uppercase tracking-[0.25em] text-[#737373]"
              >
                Password
              </label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className={inputCls}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full cursor-pointer border border-[#D4AF37] bg-[#D4AF37] px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-[#0D0D0D] transition-colors duration-300 hover:bg-[#F3E5AB] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Accesso in corso…" : "Accedi"}
            </button>
          </motion.form>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12"
          >
            <div className="mb-8 flex items-end justify-between">
              <div>
                <h1
                  data-testid="admin-candidature-title"
                  className="font-serif-lux text-2xl text-[#D4AF37] sm:text-3xl"
                >
                  Candidature Mentorship
                </h1>
                <p className="mt-2 text-sm text-[#737373]">
                  {data
                    ? `${data.totale} candidatur${data.totale === 1 ? "a" : "e"} ricevut${data.totale === 1 ? "a" : "e"}`
                    : "Caricamento…"}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => fetchCandidature(token)}
                  data-testid="admin-refresh-btn"
                  className="border border-[#D4AF37]/40 px-5 py-2 text-xs font-medium uppercase tracking-[0.15em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37]/10"
                >
                  Aggiorna
                </button>
                <button
                  type="button"
                  onClick={exportCsv}
                  data-testid="admin-export-csv-btn"
                  className="border border-[#B87333] bg-[#B87333] px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-[#D2853D]"
                >
                  Esporta CSV
                </button>
              </div>
            </div>

            <form
              onSubmit={saveSpots}
              data-testid="admin-spots-form"
              className="mb-10 flex flex-wrap items-center gap-4 border border-[#D4AF37]/20 bg-[#141414] p-5"
            >
              <label
                htmlFor="spots-total"
                className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#737373]"
              >
                Posti Mentorship Totali
              </label>
              <input
                id="spots-total"
                data-testid="admin-spots-input"
                type="number"
                min="0"
                value={spotsTotal}
                onChange={(e) => setSpotsTotal(e.target.value)}
                className="w-24 border border-white/10 bg-[#0D0D0D] px-3 py-2 text-sm text-white outline-none focus:border-[#D4AF37]"
              />
              <button
                type="submit"
                disabled={savingSpots}
                data-testid="admin-spots-save-btn"
                className="border border-[#D4AF37] bg-[#D4AF37] px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[#0D0D0D] transition-colors duration-300 hover:bg-[#F3E5AB] disabled:opacity-60"
              >
                {savingSpots ? "Salvataggio…" : "Salva"}
              </button>
              {spotsInfo && (
                <span
                  data-testid="admin-spots-info"
                  className="text-xs text-[#737373]"
                >
                  Attualmente rimasti:{" "}
                  <span className="text-[#D4AF37]">{spotsInfo.rimanenti}</span>
                </span>
              )}
            </form>

            {data && data.candidature.length === 0 && (
              <p
                data-testid="admin-empty-state"
                className="border border-white/10 bg-[#141414] p-10 text-center text-sm text-[#737373]"
              >
                Nessuna candidatura ricevuta finora.
              </p>
            )}

            {data && data.candidature.length > 0 && (
              <div className="space-y-4" data-testid="admin-candidature-list">
                {data.candidature.map((c) => (
                  <article
                    key={c.id}
                    data-testid={`candidatura-row-${c.id}`}
                    className="grid gap-4 border border-white/10 bg-[#141414] p-6 transition-colors duration-300 hover:border-[#D4AF37]/30 md:grid-cols-[1.2fr_1.2fr_0.6fr_2fr]"
                  >
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#737373]">
                        Nome
                      </p>
                      <p className="mt-1.5 text-sm font-semibold text-white">
                        {c.nome_completo}
                      </p>
                      <p className="mt-1 text-[11px] text-[#4d4d4d]">
                        {new Date(c.created_at).toLocaleString("it-IT")}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#737373]">
                        Email
                      </p>
                      <a
                        href={`mailto:${c.email_aziendale}`}
                        className="mt-1.5 block text-sm text-[#D4AF37] hover:text-[#F3E5AB]"
                      >
                        {c.email_aziendale}
                      </a>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#737373]">
                        Stress
                      </p>
                      <p className="mt-1.5 font-serif-lux text-2xl text-[#B87333]">
                        {c.livello_stress}
                        <span className="text-sm text-[#4d4d4d]">/10</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#737373]">
                        Blocco Fisiologico
                      </p>
                      <p className="mt-1.5 text-sm leading-relaxed text-[#A3A3A3]">
                        {c.blocco_fisiologico}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
